import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import PropTypes from "prop-types";

import { searchUrlWithHostname, webappURL } from "../../util/url";
import { getAllCategories, getSelectedCategory } from "./searchUtils";
import { useSearchManager } from "./useSearchManager";
import { organizationSchemaOrg } from '../../util/schemaOrg';

import Loading from "../../component/loading/Loading";
import Error from "../../component/error/Error";
import FilterPanel from "./searchFilterPanel/FilterPanel";
import MobileFilterPanel from "./searchFilterPanel/MobileFilterPanel";
import SortDropdown from "./searchSort/SortDropdown";
import FilterTags from "./searchFilterTags/FilterTags";
import SearchProductContainer from "./searchProductContainer/SearchProductContainer";
import useMediaQuery from "../../hook/useMediaQuery";

import IconFilter from "../../assets/icon_slider.svg";
import "./Search.css";
import MountedWrapper from "../../component/mountedWrapper/MountedWrapper";

const GET_CATEGORIES_FOR_FILTER = gql`
  query getCategoriesCached {
    getCategoriesCached(limit: 100) {
      name
      subcategories {
        id
        name
      }
    }
  }
`;

/**
 * Search Content component
 * @param hostname
 * @returns {Element}
 * @constructor
 */
export default function SearchContent({ hostname }) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");

  const {
    keyword,
    categoryName,
    filters,
    selectedSort,
    handleSortChange,
    handleRemoveFilter,
    handleClearAllFilters,
  } = useSearchManager();

  const { loading, error, data } = useQuery(GET_CATEGORIES_FOR_FILTER);

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  const allCategories = getAllCategories(data);
  const selectedCategory = getSelectedCategory(allCategories, categoryName);

  const metaTitle = `${keyword || "전체"} 검색결과 | 후루츠패밀리`;
  const metaDescription = `후루츠패밀리에서 거래 중인 ${keyword || "전체"} 세컨핸드 매물을 모아보세요. 구하기 힘든 브랜드 패션부터 하나뿐인 빈티지까지, 안전하게 중고거래하세요.`;
  const metaUrl = searchUrlWithHostname(hostname, keyword);

  const orgSchema = organizationSchemaOrg();

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <link rel="canonical" href={metaUrl} />
        <meta name="description" content={metaDescription} />
        {/* Open Graph (OG) tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${webappURL(hostname)}/logo512.png`} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:site_name" content="후루츠패밀리" />
        <meta property="og:description" content={metaDescription} />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@fruitsfamilyco" />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:image" content={`${webappURL(hostname)}/logo512.png`} />
      </Helmet>
      <div className="Search">
        <div className="Search-header">
          <h1 className="Search-title">
            {keyword ? `"${keyword}" 검색 결과` : "전체 상품"}
          </h1>
        </div>
        <div className="Search-content">
          {!isMobile && !isTablet && (
            <MountedWrapper>
              <div className="Search-sidebar">
                <FilterPanel />
              </div>
            </MountedWrapper>
          )}
          <div
            className={`Search-results ${isMobile || isTablet ? "Search-results-full" : ""}`}
          >
            {(isMobile || isTablet) && (
              <div className="Search-results-header">
                <button
                  className="Search-filter-button"
                  onClick={() => setIsFilterOpen(true)}
                >
                  <img src={IconFilter} alt="filter" />
                  필터
                </button>
                <div className="Search-sort-container">
                  <SortDropdown
                    selectedSort={selectedSort}
                    onSortChange={handleSortChange}
                  />
                </div>
              </div>
            )}
            {!isMobile && !isTablet && (
              <>
                <FilterTags
                  filters={filters}
                  onRemoveFilter={handleRemoveFilter}
                  onClearAll={handleClearAllFilters}
                  categories={allCategories}
                />
                <div className="Search-results-header">
                  <div className="Search-sort-container">
                    <SortDropdown
                      selectedSort={selectedSort}
                      onSortChange={handleSortChange}
                    />
                  </div>
                </div>
              </>
            )}
            <SearchProductContainer
              className="Search-products"
              keyword={keyword}
              category={selectedCategory}
              filters={filters}
              sortValue={selectedSort}
              schemaOrgPreview={orgSchema}
            />
          </div>
        </div>
        {(isMobile || isTablet) && (
          <MountedWrapper>
            <MobileFilterPanel
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
            />
          </MountedWrapper>
        )}
      </div>
    </>
  );
}

SearchContent.propTypes = {
  hostname: PropTypes.string.isRequired,
};
